import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, switchMap, throwError } from 'rxjs';

function isPublicOrExternalRequest(url: string): boolean {
  return url.includes('/autenticacao/refresh');
}

function sendWithBearer(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  token: string,
): ReturnType<HttpHandlerFn> {
  const authReq = req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (authService.getRefreshToken()) {
          return authService.refreshAccessToken().pipe(
            switchMap((newToken) => sendWithBearer(req, next, authService, newToken)),
            catchError((refreshErr) => {
              authService.sessionExpired();
              return throwError(() => refreshErr);
            }),
          );
        }

        authService.sessionExpired();
      }

      return throwError(() => error);
    }),
  );
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const authService = inject(AuthService);

  if (isPublicOrExternalRequest(req.url)) {
    return next(req);
  }

  const accessToken = authService.getAccessToken();
  if (accessToken) {
    return sendWithBearer(req, next, authService, accessToken);
  }

  const refreshToken = authService.getRefreshToken();
  if (!refreshToken) {
    authService.sessionExpired();
    return throwError(() => new Error('Sessão expirada'));
  }

  return authService.refreshAccessToken().pipe(
    switchMap((newToken) => sendWithBearer(req, next, authService, newToken)),
    catchError((refreshErr) => {
      authService.sessionExpired();
      return throwError(() => refreshErr);
    }),
  );
};
